import { useState, useEffect, useMemo, useCallback, useRef, type FormEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { getCurrentSession } from '../../lib/auth';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';
import {
  formatChallengeVideoDate,
  getYouTubeAutoplayEmbedUrl,
  getYouTubeWatchUrl,
  normalizeChallengeVideoRecord,
  parseYouTubeVideoId,
  type ChallengeVideoRecord,
} from '../../lib/challengeVideos';
import {
  calculateChallengeMetrics,
  calculateContribution,
} from '../../lib/challengeCalculations';
import {
  type ChallengeSettingsForm,
  type ChallengeStatus,
  formatChallengeChartDate,
  formatChallengeDateRange,
  formatChallengeStartDate,
  formatCurrencyForInput,
  formatCurrencyWhileTyping,
  parseCurrencyInput,
  parseYouTubePlaylistId,
  validateChallengeSettings,
} from '../../lib/challengeManagement';
import {
  PlusCircle, Sliders, Wallet, Briefcase, Calendar, Hash,
  Target, Gauge, CheckCircle, Trophy, Zap,
  Search, Plus, Trash2, Edit3, X, Play, Tv, Check, ThumbsUp,
  ArrowDownRight, ArrowUpRight, Grip
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip, BarChart, Bar, Cell, CartesianGrid
} from 'recharts';

type TradeSide = 'LONG' | 'SHORT';
type EditableTradeField = 'setup' | 'notes';
type ChartMode = 'equity' | 'chronology';
type ChallengeTrade = {
  id: number;
  date: string;
  symbol: string;
  side: TradeSide;
  open: number;
  close: number;
  pnl: number;
  setup: string;
  notes: string;
};
type ChallengeVideo = { id: string; title: string; date: string; youtubeId: string };
type VideoDragGhost = {
  videoId: string;
  left: number;
  top: number;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
};
type ChallengeState = {
  dataVersion: number;
  challengeName: string;
  challengeStatus: ChallengeStatus;
  startingBalance: number;
  targetGoal: number;
  brokerName: string;
  startDate: string;
  endDate: string;
  tradingStartTime: string;
  tradingEndTime: string;
  timeZone: string;
  youtubePlaylistUrl: string;
  autoSyncLikes: boolean;
  youtubeLikes: number;
  contributionPerLike: number;
  maximumContribution: number;
  youtubeLastSynced: string | null;
  trades: ChallengeTrade[];
  videos: ChallengeVideo[];
};
type TradeForm = Omit<ChallengeTrade, 'id' | 'open' | 'close' | 'pnl'> & { open: string; close: string; pnl: string };
type ChallengesProps = { theme?: 'light' | 'dark'; isAdmin?: boolean };

const DEFAULT_STATE: ChallengeState = {
  dataVersion: 2,
  challengeName: 'Small Account Challenge',
  challengeStatus: 'active',
  startingBalance: 2000.00,
  targetGoal: 23000.00,
  brokerName: "Interactive Brokers",
  startDate: '2026-06-01',
  endDate: '2026-06-30',
  tradingStartTime: '06:00',
  tradingEndTime: '10:00',
  timeZone: 'America/Los_Angeles',
  youtubePlaylistUrl: '',
  autoSyncLikes: false,
  youtubeLikes: 20000,
  contributionPerLike: 1,
  maximumContribution: 20000,
  youtubeLastSynced: null,
  trades: [
    {
      id: 1,
      date: "Jun 3, 2026",
      symbol: "AAPL",
      side: "LONG",
      open: 182.40,
      close: 185.10,
      pnl: 540.00,
      setup: "Opening Range Breakout",
      notes: "Clean entry after first pullback"
    },
    {
      id: 2,
      date: "Jun 5, 2026",
      symbol: "TSLA",
      side: "SHORT",
      open: 178.20,
      close: 176.00,
      pnl: -420.00,
      setup: "Pullback",
      notes: "Stopped out, entry was late"
    },
    {
      id: 3,
      date: "Jun 7, 2026",
      symbol: "NVDA",
      side: "LONG",
      open: 115.50,
      close: 123.25,
      pnl: 2100.00,
      setup: "Momentum Breakout",
      notes: "Best trade of the week"
    },
    {
      id: 4,
      date: "Jun 11, 2026",
      symbol: "AMD",
      side: "LONG",
      open: 162.10,
      close: 164.25,
      pnl: 868.00,
      setup: "VWAP Reclaim",
      notes: "Good patience, clean exit"
    },
    {
      id: 5,
      date: "Jun 14, 2026",
      symbol: "META",
      side: "LONG",
      open: 502.30,
      close: 500.90,
      pnl: -491.40,
      setup: "Failed Breakout",
      notes: "Should have waited for confirmation"
    },
    {
      id: 6,
      date: "Jun 17, 2026",
      symbol: "AMZN",
      side: "LONG",
      open: 216.35,
      close: 219.10,
      pnl: 687.50,
      setup: "Premarket High Break",
      notes: "Strong volume confirmation at the breakout"
    },
    {
      id: 7,
      date: "Jun 19, 2026",
      symbol: "MSFT",
      side: "SHORT",
      open: 486.80,
      close: 488.15,
      pnl: -337.50,
      setup: "Failed Gap Fill",
      notes: "Covered quickly when the reversal failed"
    },
    {
      id: 8,
      date: "Jun 21, 2026",
      symbol: "GOOGL",
      side: "LONG",
      open: 176.20,
      close: 180.05,
      pnl: 962.50,
      setup: "Ascending Triangle",
      notes: "Clean breakout followed by steady continuation"
    },
    {
      id: 9,
      date: "Jun 24, 2026",
      symbol: "NFLX",
      side: "SHORT",
      open: 1018.40,
      close: 1020.10,
      pnl: -425.00,
      setup: "Resistance Rejection",
      notes: "Price reclaimed resistance after the initial rejection"
    },
    {
      id: 10,
      date: "Jun 27, 2026",
      symbol: "AVGO",
      side: "LONG",
      open: 249.60,
      close: 254.20,
      pnl: 1150.00,
      setup: "Bull Flag Continuation",
      notes: "Held through consolidation for the measured move"
    }
  ],
  videos: [
    { id: "seed-1", title: "Small Challenge Day 1", date: "6/3/2026", youtubeId: "h-g8M89H9-0" },
    { id: "seed-2", title: "Small Challenge Day 2", date: "6/5/2026", youtubeId: "F15C6BvJ_O4" },
    { id: "seed-3", title: "Small Challenge Day 3", date: "6/7/2026", youtubeId: "p57U-920f04" },
    { id: "seed-4", title: "Small Challenge Day 4", date: "6/11/2026", youtubeId: "K1X5627H9-8" },
    { id: "seed-5", title: "Small Challenge Day 5", date: "6/14/2026", youtubeId: "L9a80m0P-67" }
  ]
};

const TIME_ZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'PST / America/Los_Angeles' },
  { value: 'America/Denver', label: 'MST / America/Denver' },
  { value: 'America/Chicago', label: 'CST / America/Chicago' },
  { value: 'America/New_York', label: 'EST / America/New_York' },
  { value: 'UTC', label: 'UTC' },
];
const SETTINGS_INPUT_CLASS = 'w-full bg-slate-950 border border-slate-800 focus:border-blue-500 rounded-xl py-2 px-3 text-slate-200 outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60';

function settingsInputClass(hasError: boolean): string {
  return `${SETTINGS_INPUT_CLASS} ${hasError ? 'border-red-500/70 focus:border-red-400' : ''}`;
}

function settingsFormFromState(state: ChallengeState): ChallengeSettingsForm {
  return {
    challengeName: state.challengeName,
    challengeStatus: state.challengeStatus,
    startingBalance: formatCurrencyForInput(state.startingBalance),
    targetGoal: formatCurrencyForInput(state.targetGoal),
    brokerName: state.brokerName,
    startDate: state.startDate,
    endDate: state.endDate,
    tradingStartTime: state.tradingStartTime,
    tradingEndTime: state.tradingEndTime,
    timeZone: state.timeZone,
    youtubePlaylistUrl: state.youtubePlaylistUrl,
    autoSyncLikes: state.autoSyncLikes,
    youtubeLikes: String(state.youtubeLikes),
    contributionPerLike: formatCurrencyForInput(state.contributionPerLike),
    maximumContribution: formatCurrencyForInput(state.maximumContribution),
    youtubeLastSynced: state.youtubeLastSynced,
  };
}

function stateSettingsFromPayload(payload: Record<string, unknown> | null | undefined): Partial<ChallengeState> {
  const status = String(payload?.challengeStatus || '').toLowerCase();
  return {
    challengeName: String(payload?.challengeName || DEFAULT_STATE.challengeName),
    challengeStatus: (['active', 'paused', 'completed'].includes(status) ? status : DEFAULT_STATE.challengeStatus) as ChallengeStatus,
    startingBalance: Number(payload?.startingBalance) || DEFAULT_STATE.startingBalance,
    targetGoal: Number(payload?.targetGoal) || DEFAULT_STATE.targetGoal,
    brokerName: String(payload?.brokerName || DEFAULT_STATE.brokerName),
    startDate: String(payload?.startDate || DEFAULT_STATE.startDate),
    endDate: String(payload?.endDate || DEFAULT_STATE.endDate),
    tradingStartTime: String(payload?.tradingStartTime || DEFAULT_STATE.tradingStartTime).slice(0, 5),
    tradingEndTime: String(payload?.tradingEndTime || DEFAULT_STATE.tradingEndTime).slice(0, 5),
    timeZone: String(payload?.timeZone || DEFAULT_STATE.timeZone),
    youtubePlaylistUrl: String(payload?.youtubePlaylistUrl || ''),
    autoSyncLikes: Boolean(payload?.autoSyncLikes),
    youtubeLikes: Math.max(0, Math.floor(Number(payload?.youtubeLikes) || 0)),
    contributionPerLike: Math.max(0, Number(payload?.contributionPerLike) || 0),
    maximumContribution: Math.max(0, Number(payload?.maximumContribution) || 0),
    youtubeLastSynced: payload?.youtubeLastSynced ? String(payload.youtubeLastSynced) : null,
  };
}

function formatTradingTime(value: string): string {
  const [hoursText, minutesText] = value.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return value;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

function formatLastSynced(value: string | null): string {
  if (!value) return 'Not synced yet';
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return 'Not synced yet';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

function buildApiUrl(path: string): string {
  const base = String((import.meta as any).env?.VITE_API_BASE || '').trim().replace(/\/+$/, '');
  return base ? `${base}${path}` : path;
}

async function buildChallengeAuthHeaders(): Promise<Record<string, string> | null> {
  const session = await getCurrentSession();
  const userId = String(session?.user?.id || '').trim();
  if (!userId) return null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json', 'x-user-id': userId };
  const token = String(session?.access_token || '').trim();
  if (token) headers.Authorization = `Bearer ${token}`;
  return applyTenantRequestHeaders(headers);
}

export default function App({ theme = 'dark', isAdmin = false }: ChallengesProps) {
  const [appState, setAppState] = useState<ChallengeState>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem("warrior_challenge_react_state");
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as Partial<ChallengeState>;
          const savedTrades = Array.isArray(parsed.trades) ? parsed.trades : DEFAULT_STATE.trades;
          const needsSeedMigration = (parsed.dataVersion ?? 1) < DEFAULT_STATE.dataVersion;
          const additionalSeedTrades = needsSeedMigration
            ? DEFAULT_STATE.trades.filter((trade) => trade.id > 5 && !savedTrades.some((savedTrade) => savedTrade.id === trade.id))
            : [];
          return {
            ...DEFAULT_STATE,
            ...parsed,
            dataVersion: DEFAULT_STATE.dataVersion,
            trades: [...savedTrades, ...additionalSeedTrades],
          };
        } catch (error) { console.error(error); }
      }
    }
    return DEFAULT_STATE;
  });

  useEffect(() => {
    localStorage.setItem("warrior_challenge_react_state", JSON.stringify(appState));
  }, [appState]);

  const [chartMode, setChartMode] = useState<ChartMode>('equity');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddVideoOpen, setIsAddVideoOpen] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const [tradeForm, setTradeForm] = useState<TradeForm>({
    date: '', symbol: '', side: 'LONG', open: '', close: '', pnl: '', setup: '', notes: ''
  });
  const [videoForm, setVideoForm] = useState({ title: '', url: '' });
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null);
  const [draggingVideoId, setDraggingVideoId] = useState<string | null>(null);
  const [videoDragOrder, setVideoDragOrder] = useState<string[] | null>(null);
  const [videoDragGhost, setVideoDragGhost] = useState<VideoDragGhost | null>(null);
  const [settingsForm, setSettingsForm] = useState<ChallengeSettingsForm>(() => settingsFormFromState(appState));
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsSaveError, setSettingsSaveError] = useState<string | null>(null);
  const [isSyncingLikes, setIsSyncingLikes] = useState(false);
  const [syncLikesError, setSyncLikesError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState({ title: '', youtubeId: '' });
  const challengeManagementButtonRef = useRef<HTMLButtonElement | null>(null);
  const videoDragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => videoDragCleanupRef.current?.(), []);

  const openSettingsDrawer = useCallback(() => {
    setSettingsForm(settingsFormFromState(appState));
    setSettingsSaveError(null);
    setSyncLikesError(null);
    setIsSettingsOpen(true);
  }, [appState]);

  const closeSettingsDrawer = useCallback(() => {
    setIsSettingsOpen(false);
    challengeManagementButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isSettingsOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSettingsOpen]);

  const syncChallengeSettings = useCallback(async () => {
    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) return;
      const response = await fetch(buildApiUrl('/api/challenge-management'), { headers });
      if (!response.ok) return;
      const payload = await response.json();
      if (!payload?.settings) return;
      setAppState((current) => ({ ...current, ...stateSettingsFromPayload(payload.settings) }));
    } catch (error) {
      console.error('Unable to synchronize challenge settings', error);
    }
  }, []);

  const syncVideos = useCallback(async () => {
    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) return;
      const response = await fetch(buildApiUrl('/api/challenge-videos'), { headers });
      if (!response.ok) return;
      const payload = await response.json();
      const videos = ((Array.isArray(payload?.videos) ? payload.videos : []) as unknown[])
        .map((raw) => normalizeChallengeVideoRecord(raw))
        .filter((video): video is ChallengeVideoRecord => video != null)
        .map((video) => ({
          id: video.id,
          title: video.title || 'Challenge Video',
          date: formatChallengeVideoDate(video.createdAt),
          youtubeId: video.videoId,
        }));
      setAppState((current) => ({ ...current, videos }));
    } catch (error) {
      console.error('Unable to synchronize challenge videos', error);
    }
  }, []);

  useEffect(() => {
    void syncChallengeSettings();
  }, [syncChallengeSettings]);

  useEffect(() => {
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const connectLiveUpdates = async () => {
      try {
        const headers = await buildChallengeAuthHeaders();
        if (!headers || stopped) return;
        const response = await fetch(buildApiUrl('/api/challenge-videos/events'), {
          headers,
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error(`Live video updates unavailable (${response.status})`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';
          if (messages.some((message) => message.includes('event: videos-changed'))) {
            await syncVideos();
          }
        }
      } catch (error) {
        if (!stopped && !controller.signal.aborted) {
          console.error('Challenge video live update stream disconnected', error);
        }
      } finally {
        if (!stopped) reconnectTimer = setTimeout(() => { void connectLiveUpdates(); }, 2000);
      }
    };

    void syncVideos();
    void connectLiveUpdates();
    const reconciliationTimer = window.setInterval(() => { void syncVideos(); }, 15000);

    return () => {
      stopped = true;
      controller.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      window.clearInterval(reconciliationTimer);
    };
  }, [syncVideos]);

  const triggerToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  const formatCompactCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(val);
  };

  const derivedStats = useMemo(
    () => calculateChallengeMetrics(
      appState.startingBalance,
      appState.targetGoal,
      appState.trades,
      formatChallengeStartDate(appState.startDate),
    ),
    [appState.startingBalance, appState.targetGoal, appState.trades, appState.startDate],
  );

  const settingsErrors = useMemo(() => validateChallengeSettings(settingsForm), [settingsForm]);
  const settingsStartingBalance = parseCurrencyInput(settingsForm.startingBalance);
  const settingsTargetGoal = parseCurrencyInput(settingsForm.targetGoal);
  const settingsContributionPerLike = parseCurrencyInput(settingsForm.contributionPerLike);
  const settingsMaximumContribution = parseCurrencyInput(settingsForm.maximumContribution);
  const settingsLikes = Number(settingsForm.youtubeLikes);
  const calculatedContribution = calculateContribution(
    Number.isFinite(settingsLikes) ? settingsLikes : 0,
    Number.isFinite(settingsContributionPerLike) ? settingsContributionPerLike : 0,
    Number.isFinite(settingsMaximumContribution) ? settingsMaximumContribution : 0,
  );
  const isSettingsValid = Object.keys(settingsErrors).length === 0;
  const challengeDateRange = formatChallengeDateRange(appState.startDate, appState.endDate);
  const savedContribution = calculateContribution(
    appState.youtubeLikes,
    appState.contributionPerLike,
    appState.maximumContribution,
  );

  const syncPlaylistLikes = useCallback(async (playlistUrl: string, showToast = true) => {
    if (!parseYouTubePlaylistId(playlistUrl)) {
      setSyncLikesError('Enter a valid YouTube playlist URL before syncing.');
      return null;
    }

    setIsSyncingLikes(true);
    setSyncLikesError(null);
    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) throw new Error('Sign in is required to sync YouTube likes.');
      const response = await fetch(buildApiUrl('/api/challenge-management/sync-likes'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ youtubePlaylistUrl: playlistUrl }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.totalLikeCount == null) {
        throw new Error(payload?.error || 'YouTube sync is unavailable.');
      }

      const totalLikeCount = Math.max(0, Math.floor(Number(payload.totalLikeCount) || 0));
      const lastSyncedAt = String(payload.lastSyncedAt || new Date().toISOString());
      setSettingsForm((current) => ({
        ...current,
        youtubePlaylistUrl: playlistUrl,
        youtubeLikes: String(totalLikeCount),
        youtubeLastSynced: lastSyncedAt,
      }));
      setAppState((current) => ({
        ...current,
        youtubePlaylistUrl: playlistUrl,
        youtubeLikes: totalLikeCount,
        youtubeLastSynced: lastSyncedAt,
      }));
      if (showToast) triggerToast(`Synced ${totalLikeCount.toLocaleString()} likes from ${Number(payload.videoCount) || 0} videos`);
      return { totalLikeCount, lastSyncedAt };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'YouTube sync is unavailable.';
      setSyncLikesError(message.replace(/_/g, ' '));
      if (showToast) triggerToast('Unable to sync YouTube likes. Please try again.');
      return null;
    } finally {
      setIsSyncingLikes(false);
    }
  }, [triggerToast]);

  useEffect(() => {
    if (isSavingSettings || isSyncingLikes || !isAdmin || !appState.autoSyncLikes || !parseYouTubePlaylistId(appState.youtubePlaylistUrl)) return undefined;
    const syncIfStale = () => {
      const lastSyncedAt = appState.youtubeLastSynced ? Date.parse(appState.youtubeLastSynced) : 0;
      if (!Number.isFinite(lastSyncedAt) || Date.now() - lastSyncedAt >= 15 * 60 * 1000) {
        void syncPlaylistLikes(appState.youtubePlaylistUrl, false);
      }
    };
    syncIfStale();
    const interval = window.setInterval(syncIfStale, 15 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [appState.autoSyncLikes, appState.youtubeLastSynced, appState.youtubePlaylistUrl, isAdmin, isSavingSettings, isSyncingLikes, syncPlaylistLikes]);

  const handleAddTradeSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAdmin) return;
    const newTrade = {
      id: Date.now(),
      date: tradeForm.date,
      symbol: tradeForm.symbol.toUpperCase(),
      side: tradeForm.side,
      open: parseFloat(tradeForm.open),
      close: parseFloat(tradeForm.close),
      pnl: parseFloat(tradeForm.pnl),
      setup: tradeForm.setup,
      notes: tradeForm.notes || "No additional commentary recorded."
    };

    setAppState(prev => ({
      ...prev,
      trades: [...prev.trades, newTrade]
    }));
    setIsTradeModalOpen(false);
    setTradeForm({ date: '', symbol: '', side: 'LONG', open: '', close: '', pnl: '', setup: '', notes: '' });
    triggerToast(`Recorded trade for ${newTrade.symbol}`);
  };

  const handleDeleteTrade = (id: number) => {
    if (!isAdmin) return;
    setAppState(prev => ({
      ...prev,
      trades: prev.trades.filter(t => t.id !== id)
    }));
    triggerToast("Trade row deleted successfully");
  };

  const handleUpdateTradeInline = (id: number, field: EditableTradeField, value: string) => {
    if (!isAdmin) return;
    setAppState(prev => ({
      ...prev,
      trades: prev.trades.map(t => {
        if (t.id === id) {
          const cleanVal = value.trim();
          const fallback = field === 'setup' ? 'Untitled Setup' : 'No commentary listed.';
          return { ...t, [field]: cleanVal || fallback };
        }
        return t;
      })
    }));
    triggerToast("Refined trade column inline");
  };

  const handleEditableKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.currentTarget.blur();
  };

  const handleVideoSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAdmin) return;
    const rawUrl = videoForm.url.trim();
    const parsedId = /^[a-zA-Z0-9_-]{11}$/.test(rawUrl) ? rawUrl : parseYouTubeVideoId(rawUrl);
    if (!parsedId) {
      triggerToast('Enter a valid YouTube link or video ID');
      return;
    }

    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) throw new Error('Sign in is required');
      const isEditing = editingVideoId !== null;
      const response = await fetch(buildApiUrl(isEditing ? `/api/challenge-videos/${editingVideoId}` : '/api/challenge-videos'), {
        method: isEditing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({
          videoId: parsedId,
          youtubeUrl: getYouTubeWatchUrl(parsedId),
          title: videoForm.title,
        }),
      });
      if (!response.ok) throw new Error(`Unable to save video (${response.status})`);
      await syncVideos();
      setEditingVideoId(null);
      setIsAddVideoOpen(false);
      setVideoForm({ title: '', url: '' });
      triggerToast(isEditing ? 'Walkthrough changes saved' : `Imported video walkthrough: ${videoForm.title}`);
    } catch (error) {
      console.error(error);
      triggerToast('Unable to save the video. Please try again.');
    }
  };

  const handleEditVideo = (video: ChallengeVideo) => {
    if (!isAdmin) return;
    setEditingVideoId(video.id);
    setVideoForm({
      title: video.title,
      url: video.youtubeId.length === 11 ? `https://www.youtube.com/watch?v=${video.youtubeId}` : video.youtubeId
    });
    setIsAddVideoOpen(true);
  };

  const handleDeleteVideo = async (id: string) => {
    if (!isAdmin) return;
    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) throw new Error('Sign in is required');
      const response = await fetch(buildApiUrl(`/api/challenge-videos/${id}`), { method: 'DELETE', headers });
      if (!response.ok) throw new Error(`Unable to delete video (${response.status})`);
      await syncVideos();
      triggerToast('Walkthrough video removed');
    } catch (error) {
      console.error(error);
      triggerToast('Unable to remove the video. Please try again.');
    }
  };

  const persistVideoOrder = async (orderedIds: string[], previousVideos: ChallengeVideo[]) => {
    const reorderedVideos = orderedIds
      .map((id) => previousVideos.find((video) => video.id === id))
      .filter((video): video is ChallengeVideo => Boolean(video));
    setAppState((current) => ({ ...current, videos: reorderedVideos }));

    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) throw new Error('Sign in is required');
      const response = await fetch(buildApiUrl('/api/challenge-videos/order'), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ videoIds: reorderedVideos.map((video) => video.id) }),
      });
      if (!response.ok) throw new Error(`Unable to reorder videos (${response.status})`);
      triggerToast('Video order saved');
    } catch (error) {
      console.error(error);
      setAppState((current) => ({ ...current, videos: previousVideos }));
      triggerToast('Unable to save the video order. Please try again.');
      void syncVideos();
    }
  };

  const handleVideoDragStart = (event: ReactMouseEvent<HTMLButtonElement>, videoId: string) => {
    if (!isAdmin || event.button !== 0) return;
    event.preventDefault();
    videoDragCleanupRef.current?.();

    const previousVideos = appState.videos;
    const originalOrder = previousVideos.map((video) => video.id);
    const card = event.currentTarget.closest<HTMLElement>('[data-video-card-id]');
    const cardRect = card?.getBoundingClientRect();
    let pendingOrder = originalOrder;
    setDraggingVideoId(videoId);
    setVideoDragOrder(originalOrder);
    if (cardRect) {
      setVideoDragGhost({
        videoId,
        left: cardRect.left,
        top: cardRect.top,
        width: cardRect.width,
        height: cardRect.height,
        offsetX: event.clientX - cardRect.left,
        offsetY: event.clientY - cardRect.top,
      });
    }

    const updatePendingOrder = (clientX: number, clientY: number) => {
      const otherCards = Array.from(document.querySelectorAll<HTMLElement>('[data-video-card-id]'))
        .filter((candidate) => candidate.dataset.videoCardId !== videoId)
        .map((candidate) => ({
          id: candidate.dataset.videoCardId || '',
          rect: candidate.getBoundingClientRect(),
        }))
        .filter((candidate) => candidate.id && candidate.rect.width > 0 && candidate.rect.height > 0)
        .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left);

      let insertionIndex = otherCards.length;
      for (let index = 0; index < otherCards.length; index += 1) {
        const { rect } = otherCards[index];
        const rowThreshold = rect.top + rect.height * 0.25;
        const horizontalMidpoint = rect.left + rect.width / 2;
        if (clientY < rowThreshold || (clientY <= rect.bottom && clientX < horizontalMidpoint)) {
          insertionIndex = index;
          break;
        }
      }

      const nextOrder = otherCards.map((candidate) => candidate.id);
      nextOrder.splice(insertionIndex, 0, videoId);
      if (nextOrder.length !== originalOrder.length || nextOrder.every((id, index) => id === pendingOrder[index])) return;
      pendingOrder = nextOrder;
      setVideoDragOrder(nextOrder);
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      setVideoDragGhost((current) => current ? {
        ...current,
        left: moveEvent.clientX - current.offsetX,
        top: moveEvent.clientY - current.offsetY,
      } : current);
      updatePendingOrder(moveEvent.clientX, moveEvent.clientY);
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      videoDragCleanupRef.current = null;
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      updatePendingOrder(upEvent.clientX, upEvent.clientY);
      cleanup();
      setDraggingVideoId(null);
      setVideoDragOrder(null);
      setVideoDragGhost(null);
      const orderChanged = pendingOrder.some((id, index) => id !== originalOrder[index]);
      if (orderChanged) void persistVideoOrder(pendingOrder, previousVideos);
    };

    videoDragCleanupRef.current = cleanup;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleSaveSettings = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!isAdmin) return;
    if (!isSettingsValid) {
      triggerToast('Review the highlighted challenge settings.');
      return;
    }

    const nextSettings = {
      challengeName: settingsForm.challengeName.trim(),
      challengeStatus: settingsForm.challengeStatus,
      startingBalance: settingsStartingBalance,
      targetGoal: settingsTargetGoal,
      brokerName: settingsForm.brokerName.trim(),
      startDate: settingsForm.startDate,
      endDate: settingsForm.endDate,
      tradingStartTime: settingsForm.tradingStartTime,
      tradingEndTime: settingsForm.tradingEndTime,
      timeZone: settingsForm.timeZone,
      youtubePlaylistUrl: settingsForm.youtubePlaylistUrl.trim(),
      autoSyncLikes: settingsForm.autoSyncLikes,
      youtubeLikes: Math.floor(settingsLikes),
      contributionPerLike: settingsContributionPerLike,
      maximumContribution: settingsMaximumContribution,
      youtubeLastSynced: settingsForm.youtubeLastSynced,
    };

    setSettingsSaveError(null);
    setIsSavingSettings(true);
    try {
      const headers = await buildChallengeAuthHeaders();
      if (!headers) throw new Error('Sign in is required to save challenge settings.');
      const response = await fetch(buildApiUrl('/api/challenge-management'), {
        method: 'PUT',
        headers,
        body: JSON.stringify(nextSettings),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || `Unable to save settings (${response.status})`);

      const savedSettings = payload?.settings ? stateSettingsFromPayload(payload.settings) : nextSettings;
      setAppState((current) => ({ ...current, ...savedSettings }));
      setSyncLikesError(null);
      closeSettingsDrawer();
      triggerToast('Challenge settings saved');
      if (nextSettings.autoSyncLikes) {
        void syncPlaylistLikes(nextSettings.youtubePlaylistUrl, false);
      }
    } catch (error) {
      console.error(error);
      const message = 'Unable to save challenge settings. Please try again.';
      setSettingsSaveError(message);
      triggerToast(message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleOpenAddVideo = () => {
    if (!isAdmin) return;
    setEditingVideoId(null);
    const nextSeq = appState.videos.length + 1;
    setVideoForm({
      title: `Small Challenge Day ${nextSeq}`,
      url: ''
    });
    setIsAddVideoOpen(true);
  };

  const filteredTrades = useMemo(() => {
    if (!searchQuery) return appState.trades;
    const lowerQuery = searchQuery.toLowerCase();
    return appState.trades.filter(t =>
      t.symbol.toLowerCase().includes(lowerQuery) ||
      t.setup.toLowerCase().includes(lowerQuery) ||
      t.notes.toLowerCase().includes(lowerQuery)
    );
  }, [appState.trades, searchQuery]);
  const isLightTheme = theme === 'light';
  const chartGridColor = isLightTheme ? '#cbd5e1' : '#1e293b';
  const chartAxisColor = isLightTheme ? '#475569' : '#64748b';
  const progressTrackColor = isLightTheme ? '#dbe4ef' : '#1e293b';
  const dragGhostVideo = videoDragGhost
    ? appState.videos.find((video) => video.id === videoDragGhost.videoId)
    : null;

  return (
    <div className={`challenge-page min-h-screen pb-16 font-sans antialiased selection:bg-cyan-500/30 selection:text-white ${
      isLightTheme
        ? 'challenge-page-light bg-slate-50 text-slate-950'
        : 'challenge-page-dark bg-[#050914] text-[#f8fafc]'
    }`}>
      {/* Structural alignment overrides referenced from image_9a6cdc.jpg */}
      <style>{`
        .challenge-page .glass-panel {
          background: rgba(12, 19, 36, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .challenge-page .glow-hover:hover {
          box-shadow: 0 0 20px rgba(0, 210, 255, 0.15);
          border-color: rgba(0, 210, 255, 0.3);
        }
        .challenge-page .challenge-section-heading {
          font-family: "Inter Variable", Arial, sans-serif;
        }
        .challenge-page .challenge-stat-card {
          min-width: 0;
          display: grid;
          grid-template-rows: 2rem minmax(0, 1fr) auto;
          row-gap: 0.375rem;
        }
        .challenge-page .challenge-stat-card__main {
          display: contents;
        }
        .challenge-page .challenge-stat-card__label {
          min-height: 2rem;
          align-items: flex-start;
          line-height: 1rem;
        }
        .challenge-page .challenge-stat-card__value {
          min-width: 0;
          width: 100%;
          align-self: center;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
          white-space: nowrap;
        }
        .challenge-page .challenge-stat-card__footer {
          width: 100%;
          align-self: end;
          align-items: center;
          justify-content: center;
          column-gap: 0.25rem;
          text-align: center;
        }
        @media (min-width: 1024px) and (max-width: 1535px) {
          .challenge-page .challenge-stat-card {
            padding: 0.75rem;
          }
          .challenge-page .challenge-stat-card__label,
          .challenge-page .challenge-stat-card__footer {
            font-size: 0.625rem;
          }
          .challenge-page .challenge-stat-card__label {
            letter-spacing: 0.04em;
          }
          .challenge-page .challenge-stat-card__value {
            font-size: 1.125rem;
          }
          .challenge-page .challenge-stat-card__metric {
            font-size: 1.25rem;
          }
        }
        .challenge-page-light .glass-panel {
          background: rgba(255, 255, 255, 0.92);
          border-color: rgba(148, 163, 184, 0.3);
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
        }
        .challenge-page-light [class*="text-white"] {
          color: #0f172a !important;
        }
        .challenge-page-light [class*="text-slate-100"],
        .challenge-page-light [class*="text-slate-200"] {
          color: #1e293b !important;
        }
        .challenge-page-light [class*="text-slate-300"] {
          color: #334155 !important;
        }
        .challenge-page-light [class*="text-slate-400"] {
          color: #475569 !important;
        }
        .challenge-page-light [class*="text-slate-500"],
        .challenge-page-light [class*="text-slate-600"] {
          color: #64748b !important;
        }
        .challenge-page-light .challenge-trades-header {
          color: #ffffff !important;
        }
        .challenge-page-light table td:nth-child(7),
        .challenge-page-light table td:nth-child(8) {
          background-color: #ffffff !important;
        }
        .challenge-page-light .challenge-video-stats {
          background-color: #ffffff !important;
        }
        .challenge-page-light [class*="bg-slate-950"] {
          background-color: rgba(241, 245, 249, 0.92) !important;
        }
        .challenge-page-light [class*="bg-slate-900"] {
          background-color: rgba(248, 250, 252, 0.96) !important;
        }
        .challenge-page-light [class*="bg-slate-800"] {
          background-color: rgba(226, 232, 240, 0.9) !important;
        }
        .challenge-page-light [class*="border-slate-"] {
          border-color: rgba(148, 163, 184, 0.38) !important;
        }
        .challenge-page-light [class*="divide-slate-"] > :not([hidden]) ~ :not([hidden]) {
          border-color: rgba(148, 163, 184, 0.32) !important;
        }
        .challenge-page-light [class*="bg-[#050914]"]:not(.fixed),
        .challenge-page-light [class*="bg-[#0c1324]"] {
          background-color: #f8fafc !important;
        }
        .challenge-page-light input,
        .challenge-page-light select,
        .challenge-page-light textarea {
          color: #0f172a;
          caret-color: #0891b2;
        }
        .challenge-page ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .challenge-page ::-webkit-scrollbar-track {
          background: ${isLightTheme ? '#f1f5f9' : '#050914'};
        }
        .challenge-page ::-webkit-scrollbar-thumb {
          background: ${isLightTheme ? '#cbd5e1' : '#1e293b'};
          border-radius: 4px;
        }
        .challenge-page ::-webkit-scrollbar-thumb:hover {
          background: ${isLightTheme ? '#94a3b8' : '#334155'};
        }
      `}</style>

      {}
      <main className="w-full px-4 sm:px-6 lg:px-8 mt-8 space-y-8">

        {/* Centered title banner matching exact alignments */}
        <div className="relative flex flex-col items-center text-center pt-4 pb-6 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-slate-700/80 after:to-transparent">
          <div className="flex flex-col items-center gap-1.5">
            <h1 className="text-3xl md:text-4xl font-bold [font-family:Arial,sans-serif] text-white tracking-tight mt-1">{appState.challengeName}</h1>
            <p className="text-slate-400 text-xs md:text-sm mt-0.5">
              Traded daily between {formatTradingTime(appState.tradingStartTime)}–{formatTradingTime(appState.tradingEndTime)} {TIME_ZONE_OPTIONS.find((option) => option.value === appState.timeZone)?.label.split('/')[0].trim() || appState.timeZone} (during the challenge period)
            </p>
            <span
              data-testid="challenge-status-badge"
              className={`mt-1 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${appState.challengeStatus === 'active'
                ? isLightTheme
                  ? 'border-emerald-500/70 bg-emerald-100 text-emerald-700 shadow-sm shadow-emerald-500/20'
                  : 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300'
                : appState.challengeStatus === 'paused'
                  ? 'border-amber-500/30 bg-amber-500/15 text-amber-300'
                  : 'border-slate-600/40 bg-slate-700/20 text-slate-300'
                }`}
            >
              {appState.challengeStatus}
            </span>
          </div>

          {isAdmin && <div className="mt-4 md:mt-0 md:absolute md:right-0 md:top-1/2 md:-translate-y-1/2 flex items-center gap-2.5">
            <button
              ref={challengeManagementButtonRef}
              onClick={openSettingsDrawer}
              className="flex items-center gap-2 bg-[#ffffff] hover:bg-[#bfbfbf] text-[rgba(12,19,36,0.7)] hover:text-[#0c1324] font-medium py-2.5 px-4 rounded-xl transition-all border border-[#0da6d8] hover:border-[#0b96c3] text-xs whitespace-nowrap"
            >
              <Sliders className="w-4 h-4" /> Challenge Management
            </button>
          </div>}
        </div>

        {/* STATS MATRIX COUNTERS */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 xl:gap-4">

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group border-l-4 border-l-[#0070f3]">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Current Balance</span>
                <Wallet className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold tracking-tight text-white">
                {formatCurrency(derivedStats.currentBalance)}
              </div>
            </div>
            <div className="challenge-stat-card__footer mt-2 flex justify-between text-xs">
              <span className="text-slate-400">Remaining to goal:</span>
              <span className="font-bold text-[#0da6d8]">
                {formatCompactCurrency(derivedStats.remainingBalance)}
              </span>
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Broker</span>
                <Briefcase className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value text-base xl:text-lg font-medium text-slate-100 truncate">{appState.brokerName}</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-500">
              <span className="text-slate-300">Direct market access</span>
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Date Range</span>
                <Calendar className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value text-sm xl:text-base font-medium text-slate-100">{challengeDateRange}</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-500">
              <span className="text-slate-300">30-day challenge</span>
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Trades</span>
                <Hash className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold text-slate-100">{derivedStats.totalTrades}</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-500">
              Avg/Trading Day: <span className="text-slate-300">{Math.round(derivedStats.averageTradesPerDay)} trades</span>
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Starting Balance</span>
                <CheckCircle className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold text-slate-300">{formatCurrency(appState.startingBalance)}</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-500">
              Initial challenge capital
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Target Goal</span>
                <Target className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold text-[#0da6d8]">{formatCurrency(appState.targetGoal)}</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-500">
              Challenge cap
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>ACCOUNT GROWTH</span>
                <Gauge className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className={`challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold ${derivedStats.accountGrowthPct >= 0 ? 'text-[#0da6d8]' : 'text-red-400'}`}>
                {derivedStats.accountGrowthPct >= 0 ? '+' : ''}{derivedStats.accountGrowthPct.toFixed(1)}%
              </div>
            </div>
            <div className="challenge-stat-card__footer mt-2 text-xs text-slate-400">
              Growth Rate: <span className="font-bold text-[#0da6d8]">{derivedStats.growthMultiplier.toFixed(2)}x</span>
            </div>
          </div>

          <div className="challenge-stat-card glass-panel rounded-xl p-4 glow-hover transition-all duration-300 relative overflow-hidden group border-r-4 border-r-emerald-500">
            <div className="challenge-stat-card__main">
              <div className="challenge-stat-card__label flex justify-between text-slate-400 text-xs font-semibold tracking-wider uppercase mb-1">
                <span>Win Rate</span>
                <CheckCircle className="w-4 h-4 text-[#0da6d8]" />
              </div>
              <div className="challenge-stat-card__value challenge-stat-card__metric text-xl xl:text-[1.625rem] font-bold text-emerald-400">{derivedStats.winRate}%</div>
            </div>
            <div className="challenge-stat-card__footer mt-2 flex justify-between text-xs">
              <span className="text-slate-400">Max Drawdown:</span>
              <span className="font-bold text-red-400">{formatCurrency(derivedStats.maxDrawdown)}</span>
            </div>
          </div>

        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* CHARTS CONTAINER */}
          <div className="glass-panel rounded-2xl p-6 lg:col-span-2 shadow-xl border border-slate-800/80 relative flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="challenge-section-heading text-xl font-bold text-white">Account Progress</h2>
                <p className="text-xs text-slate-400 mt-0.5">Account balance throughout the challenge</p>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-900/60 p-1 rounded-xl border border-slate-800/60">
                <button
                  onClick={() => setChartMode('equity')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all duration-200 ${chartMode === 'equity' ? 'text-white bg-slate-800 shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Equity Value
                </button>
                <button
                  onClick={() => setChartMode('chronology')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all duration-200 ${chartMode === 'chronology' ? 'text-white bg-slate-800 shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Trades Chronology
                </button>
              </div>
            </div>

            <div className="w-full flex-1 min-h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                {chartMode === 'equity' ? (
                  <AreaChart data={derivedStats.equityTrajectory} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0070f3" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#00d2ff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={chartGridColor} strokeDasharray="4" vertical={false} />
                    <XAxis dataKey="name" stroke={chartAxisColor} tickLine={false} tickMargin={12} height={36} style={{ fontSize: 11, fontFamily: 'monospace' }} tickFormatter={formatChallengeChartDate} />
                    <YAxis stroke={chartAxisColor} tickLine={false} style={{ fontSize: 11 }} tickFormatter={(val) => `$${Math.round(val).toLocaleString()}`} />
                    <Tooltip
                      contentStyle={{ background: '#0c1324', borderColor: '#1e293b', borderRadius: 8 }}
                      labelStyle={{ color: '#94a3b8', fontWeight: 'bold' }}
                      formatter={(val) => [formatCurrency(Number(val)), "Account Equity"]}
                    />
                    <Area type="monotone" dataKey="balance" stroke="#00d2ff" strokeWidth={3.5} fill="url(#equityGrad)" />
                  </AreaChart>
                ) : (
                  <BarChart data={derivedStats.chronologySeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke={chartGridColor} strokeDasharray="4" vertical={false} />
                    <XAxis dataKey="name" stroke={chartAxisColor} tickLine={false} tickMargin={12} height={36} style={{ fontSize: 10 }} />
                    <YAxis stroke={chartAxisColor} tickLine={false} style={{ fontSize: 11 }} tickFormatter={(val) => `$${val}`} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload.tradeData;
                          const isProfit = data.pnl >= 0;
                          return (
                            <div className="p-3 bg-[#0c1324] border border-slate-800 rounded-lg shadow-xl text-xs space-y-1">
                              <div className="flex items-center justify-between gap-4 border-b border-slate-800 pb-1.5 mb-1.5">
                                <span className="font-extrabold text-white">{data.symbol} ({data.date})</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${isProfit ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{data.side}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-400">Net Return:</span>
                                <span className={`font-mono font-bold ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {isProfit ? '+' : ''}{formatCurrency(data.pnl)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-400">Setup:</span>
                                <span className="text-blue-400 font-semibold">{data.setup}</span>
                              </div>
                              <div className="text-[10px] text-slate-500 italic max-w-[200px] mt-1">{data.notes}</div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                      {derivedStats.chronologySeries.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          {/* PROGRESS RADIAL TARGETS */}
          <div className="flex flex-col gap-6">
            <div className="glass-panel rounded-2xl p-6 flex-1 flex flex-col justify-between shadow-xl border border-slate-800/80 relative overflow-hidden group">
              <div className="absolute -right-20 -top-20 w-44 h-44 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all"></div>

              <div>
                <h3 className="text-sm font-bold text-slate-400 tracking-wider uppercase">Path to Target Goal</h3>

                <div className="mt-6 flex flex-col items-center">
                  <div className="relative w-36 h-36 flex items-center justify-center">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="72" cy="72" r="62" stroke={progressTrackColor} strokeWidth="8" fill="transparent" />
                      <circle
                        cx="72" cy="72" r="62"
                        stroke="#0da6d8" strokeWidth="10" fill="transparent"
                        strokeDasharray="389"
                        strokeDashoffset={389 - (derivedStats.progressPct / 100) * 389}
                        strokeLinecap="round"
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute flex flex-col items-center text-center">
                      <span className="text-3xl font-semibold text-white">{derivedStats.progressPct}%</span>
                      <span className="text-[10px] text-slate-400 font-normal tracking-widest uppercase">Completed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
                  <span className="text-slate-400">Current Balance:</span>
                  <span className="text-sm font-bold text-slate-100">{formatCurrency(derivedStats.currentBalance)}</span>
                </div>
                <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
                  <span className="text-slate-400">Total Gain:</span>
                  <span className={`text-sm font-bold ${derivedStats.cumulativePnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {derivedStats.cumulativePnl >= 0 ? '+' : ''}{formatCurrency(derivedStats.cumulativePnl)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Remaining to {formatCurrency(appState.targetGoal)}:</span>
                  <span className="text-sm font-bold text-slate-400">
                    {formatCurrency(derivedStats.remainingBalance)}
                  </span>
                </div>
              </div>
            </div>

            <div className="glass-panel rounded-2xl p-6 shadow-xl border border-slate-800/80">
              <h3 className="text-sm font-bold text-slate-400 tracking-wider uppercase mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-400" /> Performance Insights
              </h3>
              <div className="divide-y divide-slate-800/80 text-xs">
                <div className="flex items-center justify-between gap-4 pb-2.5">
                  <span className="text-slate-400">Average winning trade</span>
                  <strong className="text-sm text-emerald-400">+{formatCurrency(derivedStats.averageWin)}</strong>
                </div>
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-slate-400">Average losing trade</span>
                  <strong className="text-sm text-red-400">{formatCurrency(derivedStats.averageLoss)}</strong>
                </div>
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-slate-400">Average win/loss ratio</span>
                  <strong className="text-sm text-[#0da6d8]">{derivedStats.averageWinLossRatio.toFixed(2)}×</strong>
                </div>
                <div className="pt-2.5 flex items-center justify-between gap-4">
                  <span className="text-slate-400">Largest losing trade</span>
                  <strong className="text-sm text-red-400">{formatCurrency(derivedStats.maxDrawdown)}</strong>
                </div>
              </div>
            </div>
          </div>

        </div>

        {}
        <div className="glass-panel rounded-2xl shadow-xl border border-slate-800/80 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-800 bg-slate-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="challenge-section-heading text-xl font-bold text-white">Trades Log</h2>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by symbol, setup, etc."
                  className="w-full bg-slate-950/80 hover:bg-slate-950 focus:bg-slate-950 border border-slate-800 focus:border-blue-500/80 rounded-xl py-2 pl-10 pr-4 text-xs font-medium text-slate-200 outline-none transition-all placeholder:text-slate-500"
                />
              </div>
              {isAdmin && <button
                onClick={() => setIsTradeModalOpen(true)}
                className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:border-blue-500/50 py-2 px-3.5 rounded-xl transition-all font-medium text-xs"
              >
                <Plus className="w-3.5 h-3.5" /> Add Entry
              </button>}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-separate border-spacing-0">
              <thead>
                <tr className={`challenge-trades-header ${isLightTheme ? 'bg-[#0da6d8] text-white' : 'bg-[#0a2a5a] text-slate-100'} uppercase text-[11px] font-bold tracking-widest`}>
                  <th className="py-3 px-6 text-center border-b border-r border-border">Date</th>
                  <th className="py-3 px-4 text-center border-b border-r border-border">Symbol</th>
                  <th className="py-3 px-4 text-center border-b border-r border-border">Side</th>
                  <th className="py-3 px-4 text-center border-b border-r border-border">Open</th>
                  <th className="py-3 px-4 text-center border-b border-r border-border">Close</th>
                  <th className="py-3 px-6 text-center border-b border-r border-border">P&amp;L</th>
                  <th className="py-3 px-6 text-center border-b border-r border-border">Tactical Setup</th>
                  <th className="py-3 px-6 text-center border-b border-r border-border">Review / Notes</th>
                  {isAdmin && <th className="py-3 px-6 text-center border-b border-border">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTrades.map((trade) => {
                  const isLoss = trade.pnl < 0;
                  return (
                    <tr key={trade.id} className="transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                      <td className="px-4 py-3 text-center font-light border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10">
                        <span className="text-sm font-semibold text-zinc-700 dark:text-white/85">{trade.date}</span>
                      </td>
                      <td className="px-4 py-3 text-center font-light border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10">
                        <span className="font-medium tracking-wide text-[#0da6d8] dark:text-blue-300">{trade.symbol}</span>
                      </td>
                      <td className="px-4 py-3 text-center font-light border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10">
                        <div className="flex items-center justify-center w-full">
                          <span className={`inline-flex w-20 items-center justify-center gap-1 rounded-md border p-1.5 text-xs font-semibold leading-none whitespace-nowrap ${
                            trade.side === 'LONG'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                              : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                          }`}>
                            {trade.side === 'LONG' ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                            <span className="text-[11px] uppercase tracking-wide leading-none">{trade.side}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center font-light text-zinc-700 dark:text-zinc-200 border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10">{formatCurrency(trade.open)}</td>
                      <td className="px-4 py-3 text-center font-light text-zinc-700 dark:text-zinc-200 border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10">{formatCurrency(trade.close)}</td>
                      <td className={`px-4 py-3 text-center font-semibold border-r border-b border-border bg-zinc-50 dark:bg-blue-900/10 ${isLoss ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}
                      </td>
                      <td
                        contentEditable={isAdmin}
                        suppressContentEditableWarning
                        onBlur={(e) => handleUpdateTradeInline(trade.id, 'setup', e.target.innerText)}
                        onKeyDown={handleEditableKeyDown}
                        className={`px-4 py-3 text-center font-light text-black dark:text-zinc-200 border-r border-b border-border bg-white dark:bg-blue-900/10 min-w-[140px] ${isAdmin ? 'hover:bg-slate-800/50 focus:bg-slate-900/80 focus:ring-1 focus:ring-blue-500 rounded-lg outline-none transition-all cursor-text' : ''}`}
                        title={isAdmin ? 'Click to edit setup inline' : undefined}
                      >
                        {trade.setup}
                      </td>
                      <td
                        contentEditable={isAdmin}
                        suppressContentEditableWarning
                        onBlur={(e) => handleUpdateTradeInline(trade.id, 'notes', e.target.innerText)}
                        onKeyDown={handleEditableKeyDown}
                        className={`px-4 py-3 text-center font-light text-black dark:text-zinc-300 border-r border-b border-border bg-white dark:bg-blue-900/10 max-w-[250px] truncate ${isAdmin ? 'hover:bg-slate-800/50 focus:bg-slate-900/80 focus:ring-1 focus:ring-blue-500 rounded-lg outline-none transition-all cursor-text focus:whitespace-normal focus:max-w-none' : ''}`}
                        title={isAdmin ? 'Click to edit notes inline' : undefined}
                      >
                        {trade.notes}
                      </td>
                      {isAdmin && <td className="px-4 py-3 text-center font-light border-b border-border bg-zinc-50 dark:bg-blue-900/10">
                        <button
                          onClick={() => handleDeleteTrade(trade.id)}
                          className="text-slate-500 hover:text-red-400 p-1 rounded-lg hover:bg-red-500/10 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredTrades.length === 0 && (
            <div className="py-16 text-center">
              <Tv className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <h4 className="text-sm font-bold text-slate-300">No matching trades found</h4>
              <p className="text-xs text-slate-500 mt-1">Try updating your search query or manual log a new trade!</p>
            </div>
          )}
        </div>

        {}
        <div className="pt-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <h2 className="challenge-section-heading text-xl font-extrabold text-white flex items-center gap-2">
                <Tv className="w-5 h-5 text-blue-500" /> Video Challenge Logs
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">Watch small account challenge videos</p>
            </div>

            <div className="challenge-video-stats flex flex-wrap items-center gap-4 sm:gap-5 bg-slate-950/40 px-5 py-3 rounded-full border border-slate-800/90 shadow-lg">
              <div className="flex items-center gap-3">
                <ThumbsUp className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold text-slate-400">YouTube Likes:</span>
                <span className="text-base font-bold text-white">{appState.youtubeLikes.toLocaleString()}</span>
              </div>
              <div className="hidden sm:block h-6 w-px bg-slate-800" />
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-semibold text-slate-400">Ross's Contribution:</span>
                <span className="text-base font-bold text-emerald-400">{formatCurrency(savedContribution)}</span>
                <span className="text-xs font-medium text-slate-500">({formatCurrency(appState.contributionPerLike)}/like)</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {appState.videos.map((video, index) => {
              const thumbnailSrc = video.youtubeId && video.youtubeId !== "dQw4w9WgXcQ"
                ? `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`
                : `https://placehold.co/600x337/0c1324/00d2ff?text=Day+${index + 1}`;
              return (
                <div
                  key={video.id}
                  data-video-card-id={video.id}
                  style={{ order: videoDragOrder ? videoDragOrder.indexOf(video.id) : index }}
                  className={`group glass-panel min-h-[220px] rounded-2xl overflow-hidden transition-all duration-200 relative border ${draggingVideoId === video.id ? 'border-2 border-dashed border-cyan-400/80 bg-cyan-500/10 shadow-[0_0_28px_rgba(34,211,238,0.18)]' : 'glow-hover border-slate-800/80'}`}
                >
                  {draggingVideoId === video.id && (
                    <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                      <span className="rounded-full border border-cyan-400/40 bg-slate-950/75 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.16em] text-cyan-300">
                        Drop here
                      </span>
                    </div>
                  )}
                  <div className={`contents ${draggingVideoId === video.id ? 'invisible' : ''}`}>
                  {isAdmin && (
                    <button
                      type="button"
                      onMouseDown={(event) => handleVideoDragStart(event, video.id)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Drag to reorder ${video.title}`}
                      title="Drag to reorder"
                      className="absolute right-2 top-2 z-20 inline-flex h-7 w-7 touch-none select-none cursor-grab items-center justify-center rounded-md border-2 border-[rgba(2,6,23,0.9)] bg-[#fafafa] text-[rgba(2,6,23,0.9)] shadow-lg backdrop-blur-sm transition-colors hover:bg-white hover:text-slate-950 active:cursor-grabbing"
                    >
                      <Grip className="h-4 w-4" />
                    </button>
                  )}
                  <div
                    onClick={() => {
                      setActiveVideo({ title: video.title, youtubeId: video.youtubeId });
                      setIsPlayerOpen(true);
                    }}
                    className="relative overflow-hidden aspect-video cursor-pointer"
                  >
                    <img src={thumbnailSrc} draggable={false} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" alt={video.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-slate-900/30 to-transparent opacity-80 group-hover:opacity-40 transition-opacity"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-[#0070f3] text-white rounded-full p-4 transform scale-90 group-hover:scale-100 opacity-90 group-hover:opacity-100 transition-all shadow-xl shadow-blue-500/40">
                        <Play className="w-6 h-6 fill-current" />
                      </div>
                    </div>

                  </div>

                  <div className="p-4 flex flex-col justify-between">
                    <div>
                      <h3
                        onClick={() => {
                          setActiveVideo({ title: video.title, youtubeId: video.youtubeId });
                          setIsPlayerOpen(true);
                        }}
                        className="font-medium text-sm text-white group-hover:text-blue-400 transition-colors cursor-pointer"
                      >
                        {video.title}
                      </h3>
                      <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Uploaded: {video.date}
                      </p>
                    </div>
                    {isAdmin && <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => handleEditVideo(video)} className="text-slate-500 hover:text-cyan-400 p-1.5 rounded-lg hover:bg-slate-800/80 transition-all">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeleteVideo(video.id)} className="text-slate-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 transition-all">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          setActiveVideo({ title: video.title, youtubeId: video.youtubeId });
                          setIsPlayerOpen(true);
                        }}
                        className="text-blue-400 hover:text-cyan-400 font-bold transition-colors"
                      >
                      </button>
                    </div>}
                  </div>
                  </div>
                </div>
              );
            })}

            {isAdmin && <div
              onClick={handleOpenAddVideo}
              className="order-last rounded-2xl border-2 border-dashed border-slate-800 hover:border-blue-500/50 hover:bg-blue-500/5 bg-slate-950/20 p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all group min-h-[220px]"
            >
              <div className="bg-slate-900 group-hover:bg-blue-500/10 p-4 rounded-full text-slate-400 group-hover:text-blue-400 transition-all mb-3 border border-slate-800">
                <Plus className="w-6 h-6" />
              </div>
              <h4
                className="text-[13px] font-semibold text-slate-200 group-hover:text-white transition-colors"
                style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}
              >
                Add Video
              </h4>
              <p className="text-[10px] text-slate-500 mt-1 max-w-[180px]">Insert YouTube link</p>
            </div>}
          </div>
        </div>

      </main>

      {videoDragGhost && dragGhostVideo && (
        <div
          data-testid="video-drag-ghost"
          aria-hidden="true"
          className="pointer-events-none fixed z-[100] overflow-hidden rounded-2xl border-2 border-cyan-300/80 bg-[#0c1324]/95 opacity-85 shadow-[0_24px_60px_rgba(0,0,0,0.55),0_0_32px_rgba(34,211,238,0.28)] backdrop-blur-sm"
          style={{
            left: videoDragGhost.left,
            top: videoDragGhost.top,
            width: videoDragGhost.width,
            height: videoDragGhost.height,
            transform: 'rotate(1.5deg) scale(0.96)',
            transformOrigin: `${videoDragGhost.offsetX}px ${videoDragGhost.offsetY}px`,
          }}
        >
          <div className="relative aspect-video overflow-hidden">
            <img
              src={dragGhostVideo.youtubeId && dragGhostVideo.youtubeId !== 'dQw4w9WgXcQ'
                ? `https://img.youtube.com/vi/${dragGhostVideo.youtubeId}/hqdefault.jpg`
                : 'https://placehold.co/600x337/0c1324/00d2ff?text=Challenge+Video'}
              alt=""
              draggable={false}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-slate-900/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-[#0070f3] p-3 text-white shadow-xl shadow-blue-500/40">
                <Play className="h-5 w-5 fill-current" />
              </div>
            </div>
          </div>
          <div className="p-4">
            <h3 className="truncate text-sm font-medium text-white">{dragGhostVideo.title}</h3>
            <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
              <Calendar className="h-3 w-3" /> Uploaded: {dragGhostVideo.date}
            </p>
          </div>
        </div>
      )}

      {/* TOAST SYSTEM */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 glass-panel border-l-4 border-l-emerald-500 py-3 px-5 rounded-xl shadow-xl flex items-center gap-3 transition-all duration-300">
          <div className="bg-emerald-500/10 p-1.5 rounded-lg text-emerald-400">
            <Check className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-bold text-white">{toast}</p>
          </div>
        </div>
      )}

      {}
      {/* MODAL: MANUAL TRADE ENTRY */}
      {isAdmin && isTradeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050914]/80 backdrop-blur-sm">
          <div className="glass-panel w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-slate-700/60 transform transition-transform duration-300">
            <div className="bg-slate-900/80 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-white tracking-widest uppercase flex items-center gap-2">
                <PlusCircle className="w-4 h-4 text-blue-500" /> Record New Trade Entry
              </h3>
              <button onClick={() => setIsTradeModalOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddTradeSubmit} className="p-6 space-y-4 text-xs font-semibold">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Date</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Jun 15, 2026"
                    value={tradeForm.date}
                    onChange={(e) => setTradeForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Ticker Symbol</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. TSLA, NVDA"
                    value={tradeForm.symbol}
                    onChange={(e) => setTradeForm(prev => ({ ...prev, symbol: e.target.value }))}
                    className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Side Action</label>
                  <select
                    value={tradeForm.side}
                    onChange={(e) => setTradeForm(prev => ({ ...prev, side: e.target.value === 'SHORT' ? 'SHORT' : 'LONG' }))}
                    className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                  >
                    <option value="LONG">LONG 🟢</option>
                    <option value="SHORT">SHORT 🔴</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Entry Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="150.00"
                    value={tradeForm.open}
                    onChange={(e) => setTradeForm(prev => ({ ...prev, open: e.target.value }))}
                    className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Exit Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="155.00"
                    value={tradeForm.close}
                    onChange={(e) => setTradeForm(prev => ({ ...prev, close: e.target.value }))}
                    className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Net Realized P&L ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="e.g. 500.00 or -250.00"
                  value={tradeForm.pnl}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, pnl: e.target.value }))}
                  className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Tactical Setup</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Opening Range Breakout"
                  value={tradeForm.setup}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, setup: e.target.value }))}
                  className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Review Notes</label>
                <textarea
                  rows={3}
                  placeholder="Mental discipline, and lesson learned..."
                  value={tradeForm.notes}
                  onChange={(e) => setTradeForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 outline-none transition-all resize-none"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                <button type="button" onClick={() => setIsTradeModalOpen(false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 px-5 rounded-xl transition-all">Cancel</button>
                <button type="submit" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-2.5 px-5 rounded-xl shadow-md transition-all">Record Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DRAWER: CHALLENGE MANAGEMENT */}
      {isAdmin && isSettingsOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/75"
          onClick={closeSettingsDrawer}
        >
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="challenge-management-title"
            className="ml-auto h-full w-full max-w-md bg-[#050914] border-l border-slate-800 shadow-2xl flex flex-col"
            onClick={(event) => event.stopPropagation()}
            onSubmit={handleSaveSettings}
            noValidate
          >
            <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-slate-800 bg-[#050914]">
              <div>
                <h2 id="challenge-management-title" className="text-sm font-extrabold text-white tracking-widest uppercase">Challenge Management</h2>
                <p className="text-[10px] text-slate-500 mt-0.5">Manage challenge details, contribution, and progress</p>
              </div>
              <button type="button" aria-label="Close Challenge Management" onClick={closeSettingsDrawer} className="text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 text-xs font-semibold">
              <section aria-labelledby="challenge-details-heading">
                <h3 id="challenge-details-heading" className="text-[10px] font-extrabold text-cyan-400 tracking-[0.18em] uppercase mb-3">Challenge Details</h3>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="challenge-name" className="block text-slate-400 uppercase tracking-wider mb-1.5">Challenge Name</label>
                    <input id="challenge-name" type="text" value={settingsForm.challengeName} onChange={(e) => setSettingsForm((current) => ({ ...current, challengeName: e.target.value }))} className={settingsInputClass(Boolean(settingsErrors.challengeName))} />
                    {settingsErrors.challengeName && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.challengeName}</p>}
                  </div>

                  <div>
                    <label htmlFor="challenge-status" className="block text-slate-400 uppercase tracking-wider mb-1.5">Challenge Status</label>
                    <select id="challenge-status" value={settingsForm.challengeStatus} onChange={(e) => setSettingsForm((current) => ({ ...current, challengeStatus: e.target.value as ChallengeStatus }))} className={SETTINGS_INPUT_CLASS}>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="starting-capital" className="block text-slate-400 uppercase tracking-wider mb-1.5">Starting Capital</label>
                      <input id="starting-capital" type="text" inputMode="decimal" value={settingsForm.startingBalance} onChange={(e) => setSettingsForm((current) => ({ ...current, startingBalance: formatCurrencyWhileTyping(e.target.value) }))} onBlur={() => Number.isFinite(settingsStartingBalance) && setSettingsForm((current) => ({ ...current, startingBalance: formatCurrencyForInput(settingsStartingBalance) }))} className={settingsInputClass(Boolean(settingsErrors.startingBalance))} />
                      {settingsErrors.startingBalance && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.startingBalance}</p>}
                    </div>
                    <div>
                      <label htmlFor="challenge-goal" className="block text-slate-400 uppercase tracking-wider mb-1.5">Challenge Capital Goal</label>
                      <input id="challenge-goal" type="text" inputMode="decimal" value={settingsForm.targetGoal} onChange={(e) => setSettingsForm((current) => ({ ...current, targetGoal: formatCurrencyWhileTyping(e.target.value) }))} onBlur={() => Number.isFinite(settingsTargetGoal) && setSettingsForm((current) => ({ ...current, targetGoal: formatCurrencyForInput(settingsTargetGoal) }))} className={settingsInputClass(Boolean(settingsErrors.targetGoal))} />
                      {settingsErrors.targetGoal && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.targetGoal}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="broker-name" className="block text-slate-400 uppercase tracking-wider mb-1.5">Broker Name</label>
                    <input id="broker-name" type="text" value={settingsForm.brokerName} onChange={(e) => setSettingsForm((current) => ({ ...current, brokerName: e.target.value }))} className={settingsInputClass(Boolean(settingsErrors.brokerName))} />
                    {settingsErrors.brokerName && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.brokerName}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="challenge-start-date" className="block text-slate-400 uppercase tracking-wider mb-1.5">Challenge Start Date</label>
                      <input id="challenge-start-date" type="date" value={settingsForm.startDate} onChange={(e) => setSettingsForm((current) => ({ ...current, startDate: e.target.value }))} className={`${settingsInputClass(Boolean(settingsErrors.startDate))} [color-scheme:dark]`} />
                      {settingsErrors.startDate && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.startDate}</p>}
                    </div>
                    <div>
                      <label htmlFor="challenge-end-date" className="block text-slate-400 uppercase tracking-wider mb-1.5">Challenge End Date</label>
                      <input id="challenge-end-date" type="date" min={settingsForm.startDate} value={settingsForm.endDate} onChange={(e) => setSettingsForm((current) => ({ ...current, endDate: e.target.value }))} className={`${settingsInputClass(Boolean(settingsErrors.endDate))} [color-scheme:dark]`} />
                      {settingsErrors.endDate && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.endDate}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="trading-start-time" className="block text-slate-400 uppercase tracking-wider mb-1.5">Trading Start Time</label>
                      <input id="trading-start-time" type="time" value={settingsForm.tradingStartTime} onChange={(e) => setSettingsForm((current) => ({ ...current, tradingStartTime: e.target.value }))} className={`${settingsInputClass(Boolean(settingsErrors.tradingStartTime))} [color-scheme:dark]`} />
                      {settingsErrors.tradingStartTime && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.tradingStartTime}</p>}
                    </div>
                    <div>
                      <label htmlFor="trading-end-time" className="block text-slate-400 uppercase tracking-wider mb-1.5">Trading End Time</label>
                      <input id="trading-end-time" type="time" value={settingsForm.tradingEndTime} onChange={(e) => setSettingsForm((current) => ({ ...current, tradingEndTime: e.target.value }))} className={`${settingsInputClass(Boolean(settingsErrors.tradingEndTime))} [color-scheme:dark]`} />
                      {settingsErrors.tradingEndTime && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.tradingEndTime}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="challenge-time-zone" className="block text-slate-400 uppercase tracking-wider mb-1.5">Time Zone</label>
                    <select id="challenge-time-zone" value={settingsForm.timeZone} onChange={(e) => setSettingsForm((current) => ({ ...current, timeZone: e.target.value }))} className={settingsInputClass(Boolean(settingsErrors.timeZone))}>
                      {TIME_ZONE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    {settingsErrors.timeZone && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.timeZone}</p>}
                  </div>
                </div>
              </section>

              <section aria-labelledby="youtube-contribution-heading" className="mt-6 pt-5 border-t border-slate-800">
                <h3 id="youtube-contribution-heading" className="text-[10px] font-extrabold text-cyan-400 tracking-[0.18em] uppercase mb-3">YouTube &amp; Contribution</h3>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="youtube-playlist-url" className="block text-slate-400 uppercase tracking-wider mb-1.5">YouTube Playlist URL</label>
                    <input id="youtube-playlist-url" type="url" placeholder="https://www.youtube.com/playlist?list=..." value={settingsForm.youtubePlaylistUrl} onChange={(e) => setSettingsForm((current) => ({ ...current, youtubePlaylistUrl: e.target.value }))} className={settingsInputClass(Boolean(settingsErrors.youtubePlaylistUrl))} />
                    <p className="mt-1 text-[10px] font-normal text-slate-500">This playlist contains all challenge videos.</p>
                    {settingsErrors.youtubePlaylistUrl && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.youtubePlaylistUrl}</p>}
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2.5">
                    <div>
                      <label htmlFor="auto-sync-likes" className="block text-slate-300 uppercase tracking-wider">Automatically Sync Video Likes</label>
                      <p className="mt-0.5 text-[10px] font-normal text-slate-500">Uses the server-side YouTube integration.</p>
                    </div>
                    <button id="auto-sync-likes" type="button" role="switch" aria-checked={settingsForm.autoSyncLikes} aria-label="Automatically Sync Video Likes" onClick={() => setSettingsForm((current) => ({ ...current, autoSyncLikes: !current.autoSyncLikes }))} className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors ${settingsForm.autoSyncLikes ? 'border-blue-400 bg-blue-500' : 'border-slate-700 bg-slate-800'}`}>
                      <span className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform ${settingsForm.autoSyncLikes ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {settingsForm.autoSyncLikes ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2.5">
                      <div className="min-w-0">
                        <span className="block text-[10px] text-slate-500 uppercase tracking-wider">Last Synced</span>
                        <span className="block truncate text-xs text-slate-300">{formatLastSynced(settingsForm.youtubeLastSynced)}</span>
                      </div>
                      <button type="button" onClick={() => void syncPlaylistLikes(settingsForm.youtubePlaylistUrl)} disabled={isSyncingLikes || Boolean(settingsErrors.youtubePlaylistUrl)} className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-cyan-400 hover:text-cyan-300 disabled:cursor-not-allowed disabled:opacity-40">
                        {isSyncingLikes ? 'Syncing…' : 'Sync Now'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="youtube-total-likes" className="block text-slate-400 uppercase tracking-wider mb-1.5">YouTube Total Likes</label>
                      <input id="youtube-total-likes" type="number" min="0" step="1" value={settingsForm.youtubeLikes} onChange={(e) => setSettingsForm((current) => ({ ...current, youtubeLikes: e.target.value }))} className={settingsInputClass(Boolean(settingsErrors.youtubeLikes))} />
                      {settingsErrors.youtubeLikes && <p role="alert" className="mt-1 text-[10px] text-red-400">{settingsErrors.youtubeLikes}</p>}
                    </div>
                  )}
                  {syncLikesError && <p role="alert" className="text-[10px] text-red-400">{syncLikesError}</p>}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contribution-per-like" className="block text-slate-400 uppercase tracking-wider mb-1.5">Contribution Per Like</label>
                      <input id="contribution-per-like" type="text" inputMode="decimal" value={settingsForm.contributionPerLike} onChange={(e) => setSettingsForm((current) => ({ ...current, contributionPerLike: formatCurrencyWhileTyping(e.target.value) }))} onBlur={() => Number.isFinite(settingsContributionPerLike) && setSettingsForm((current) => ({ ...current, contributionPerLike: formatCurrencyForInput(settingsContributionPerLike) }))} className={settingsInputClass(Boolean(settingsErrors.contributionPerLike))} />
                      {settingsErrors.contributionPerLike && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.contributionPerLike}</p>}
                    </div>
                    <div>
                      <label htmlFor="maximum-contribution" className="block text-slate-400 uppercase tracking-wider mb-1.5">Maximum Contribution</label>
                      <input id="maximum-contribution" type="text" inputMode="decimal" value={settingsForm.maximumContribution} onChange={(e) => setSettingsForm((current) => ({ ...current, maximumContribution: formatCurrencyWhileTyping(e.target.value) }))} onBlur={() => Number.isFinite(settingsMaximumContribution) && setSettingsForm((current) => ({ ...current, maximumContribution: formatCurrencyForInput(settingsMaximumContribution) }))} className={settingsInputClass(Boolean(settingsErrors.maximumContribution))} />
                      {settingsErrors.maximumContribution && <p role="alert" className="mt-1 text-[10px] leading-snug text-red-400">{settingsErrors.maximumContribution}</p>}
                    </div>
                  </div>

                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 flex items-center justify-between gap-3">
                    <div>
                      <span className="block text-[10px] text-slate-500 uppercase tracking-wider">Calculated Contribution</span>
                      <span className="text-[10px] font-normal text-slate-500">Likes × rate, capped at maximum</span>
                    </div>
                    <span className="text-sm font-extrabold font-mono text-emerald-400">{formatCurrency(calculatedContribution)}</span>
                  </div>
                </div>
              </section>

            </div>

            <div className="shrink-0 border-t border-slate-800 bg-[#050914] px-6 py-4">
              {settingsSaveError && <p role="alert" className="mb-3 text-center text-[11px] font-medium text-red-400">{settingsSaveError}</p>}
              <button type="submit" aria-busy={isSavingSettings} disabled={!isSettingsValid || isSavingSettings} className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-slate-800 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-blue-500/20">
                {isSavingSettings ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: ADD / EDIT VIDEO */}
      {isAdmin && isAddVideoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050914]/80 backdrop-blur-sm">
          <div className="glass-panel w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-slate-700/60 transform transition-transform duration-300">
            <div className="bg-slate-900/80 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-white tracking-widest uppercase flex items-center gap-2">
                <Tv className="text-blue-500 w-4 h-4" /> {editingVideoId ? "Edit Video" : "Import YouTube Video"}
              </h3>
              <button onClick={() => setIsAddVideoOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleVideoSubmit} className="p-6 space-y-5 text-xs font-semibold">
              <div>
                <label className="block text-slate-400 uppercase tracking-wider mb-1.5">Video Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Small Challenge Day 6"
                  value={videoForm.title}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 placeholder:text-slate-600/70 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-slate-400 uppercase tracking-wider mb-1.5">YouTube Link / Video URL</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                  value={videoForm.url}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 focus:border-blue-500 rounded-xl py-2.5 px-3.5 text-slate-200 placeholder:text-slate-600/70 outline-none transition-all"
                />
                <p className="text-[10px] text-slate-500 mt-1.5">Accepts share links, embed strings, or video codes</p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                <button type="button" onClick={() => setIsAddVideoOpen(false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 px-5 rounded-xl transition-all">Cancel</button>
                <button type="submit" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-2.5 px-5 rounded-xl shadow-md">
                  {editingVideoId ? "Save Changes" : "Import Video"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: NATIVE VIDEO PLAYER */}
      {isPlayerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050914]/95 backdrop-blur-md">
          <div className="glass-panel w-full max-w-4xl rounded-2xl overflow-hidden border border-slate-800">
            <div className="bg-slate-900/80 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-white uppercase tracking-widest"> {activeVideo.title}</h3>
              <button onClick={() => setIsPlayerOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative pb-[56.25%] h-0 bg-black">
              <iframe
                className="absolute inset-0 w-full h-full border-0"
                src={getYouTubeAutoplayEmbedUrl(activeVideo.youtubeId)}
                title={`YouTube video player: ${activeVideo.title || 'Challenge Video'}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="eager"
                referrerPolicy="origin"
              ></iframe>
            </div>
            {/* Premium Direct Fallback Navigation Block */}
            <div className="bg-slate-900/90 px-6 py-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-left">
                <p className="text-xs font-bold text-slate-300">Having trouble playing the video here?</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Some browser security parameters and sandboxes block inline player streams.</p>
              </div>
              <a
                href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-extrabold text-xs py-2 px-5 rounded-xl transition-all shadow-md shadow-blue-500/10"
              >
                <Play className="w-3.5 h-3.5 fill-current" /> Watch on YouTube ↗
              </a>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
